import { useEffect, useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { CheckCircle, Phone, MapPin, Copy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const OrderConfirmed = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [order, setOrder] = useState<any>(null);
  const [seller, setSeller] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!orderId || !user) return;
    let cancelled = false;

    const fetchOrder = async () => {
      const { data } = await supabase
        .from("orders")
        .select("*, listings(title, species, volume, price_per_stere, location)")
        .eq("id", orderId)
        .eq("buyer_id", user.id)
        .single();

      if (data) {
        setOrder(data);
        const { data: sellerData } = await supabase
          .from("profiles")
          .select("full_name, phone, address, location, email")
          .eq("user_id", data.seller_id)
          .single();
        setSeller(sellerData);
      }
      return data;
    };

    const verifyAndFetch = async () => {
      // First try fetching - maybe webhook already fired
      const data = await fetchOrder();
      if (data?.status === "paid" || data?.status === "completed") return;

      // Fallback: call verify-payment to check Stripe directly
      console.log("[OrderConfirmed] Order still pending, calling verify-payment fallback...");
      try {
        const { data: verifyResult, error } = await supabase.functions.invoke("verify-payment", {
          body: { order_id: orderId },
        });
        console.log("[OrderConfirmed] verify-payment result:", verifyResult, error);
      } catch (e) {
        console.error("[OrderConfirmed] verify-payment error:", e);
      }

      // Re-fetch after verification
      if (!cancelled) {
        await fetchOrder();
      }
    };

    verifyAndFetch();

    // Poll every 3s for up to 30s as additional safety net
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (cancelled || attempts > 10) {
        clearInterval(interval);
        return;
      }
      const data = await fetchOrder();
      if (data?.status === "paid" || data?.status === "completed") {
        clearInterval(interval);
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orderId, user]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-12 flex-1 max-w-2xl">
        <div className="text-center mb-8">
          <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold mb-2">Réservation confirmée !</h1>
          <p className="text-muted-foreground">Votre Wood-Pass a été généré.</p>
        </div>

        {order && (
          <div className="space-y-6">
            {/* Wood Pass */}
            <div className="bg-gradient-forest text-primary-foreground rounded-xl p-6">
              <p className="text-xs uppercase tracking-wider opacity-70 mb-1">Wood-Pass</p>
              <p className="font-mono text-2xl font-bold tracking-wider">
                WP-{order.wood_pass_code?.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-sm mt-3 opacity-80">
                {(order as any).listings?.title} · {order.volume} stères
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-3 gap-1"
                onClick={() => {
                  navigator.clipboard.writeText(`WP-${order.wood_pass_code?.slice(0, 8).toUpperCase()}`);
                  toast({ title: "Copié !" });
                }}
              >
                <Copy className="h-3 w-3" /> Copier le code
              </Button>
            </div>

            {/* Seller contact (unlocked) */}
            {seller && order.seller_contact_unlocked && (
              <div className="bg-card rounded-lg border p-6">
                <h3 className="font-display font-semibold mb-4">Contact du vendeur débloqué</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                      {seller.full_name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="font-medium">{seller.full_name}</p>
                      <p className="text-xs text-muted-foreground">{seller.email}</p>
                    </div>
                  </div>
                  {seller.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary" />
                      <a href={`tel:${seller.phone}`} className="text-primary font-medium hover:underline">{seller.phone}</a>
                    </div>
                  )}
                  {(seller.address || seller.location) && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>{seller.address || seller.location}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment summary */}
            <div className="bg-card rounded-lg border p-6">
              <h3 className="font-display font-semibold mb-3">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total bois</span>
                  <span className="font-medium">{order.total_wood_price}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Acompte payé (frais de service)</span>
                  <span className="font-medium text-primary">-{order.commission_amount}€</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-medium">Solde à payer au vendeur à la livraison</span>
                  <span className="font-bold text-lg">{order.total_wood_price - order.commission_amount}€</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">L'acompte de {order.commission_amount}€ déjà versé est déduit du prix total. Vous ne payez que le solde restant directement au vendeur à la livraison.</p>
            </div>

            <div className="flex gap-3">
              <Button asChild className="flex-1 gap-2">
                <Link to="/tableau-de-bord">
                  Tableau de bord <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="flex-1">
                <Link to="/messages">Messages</Link>
              </Button>
            </div>
          </div>
        )}

        {!order && (
          <div className="text-center text-muted-foreground py-8">
            <p>Chargement de votre commande...</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OrderConfirmed;
